import type { ColumnMapping, ColumnMappingField } from '../../types'
import { normalizeHeaderText } from '../normalization'
import { cellToText } from './cell'

export const HEADER_SYNONYMS: Readonly<Record<ColumnMappingField, readonly string[]>> = {
  itemCodeColumn: ['项目编码', '清单编码', '编码', '项目编号', '清单项目编码'],
  itemNameColumn: ['项目名称', '清单名称', '名称', '项目', '清单项目名称'],
  itemDescriptionColumn: ['项目特征', '项目特征描述', '特征描述', '特征', '工作内容'],
  unitColumn: ['计量单位', '单位'],
  quantityColumn: ['工程量', '数量', '清单工程量'],
  unitPriceColumn: ['综合单价', '单价', '含税综合单价'],
  totalPriceColumn: ['合价', '综合合价', '金额', '总价'],
}

const FIELD_ORDER = Object.keys(HEADER_SYNONYMS) as readonly ColumnMappingField[]

export interface HeaderDetection {
  /** Zero-based index in the supplied rows array. */
  rowIndex: number
  /** One-based worksheet row number. */
  headerRow: number
  mapping: ColumnMapping
  detectedFieldCount: number
  confidence: number
  score: number
}

interface HeaderCandidate {
  field: ColumnMappingField
  column: number
  score: number
  synonymLength: number
}

function matchHeaderScore(header: string, synonym: string): number {
  if (!header || !synonym) return 0
  if (header === synonym) return 100 + synonym.length
  if (synonym.length >= 4 && header.includes(synonym)) return 70 + synonym.length
  if (header.length >= 4 && synonym.includes(header)) return 50 + header.length
  return 0
}

export function emptyColumnMapping(headerRow: number | null = null): ColumnMapping {
  return {
    headerRow,
    itemCodeColumn: null,
    itemNameColumn: null,
    itemDescriptionColumn: null,
    unitColumn: null,
    quantityColumn: null,
    unitPriceColumn: null,
    totalPriceColumn: null,
  }
}

/** Detects fields in a single header row. Column indexes are zero-based. */
export function detectColumnMapping(
  headerCells: readonly unknown[],
  headerRow: number | null = 1,
): ColumnMapping {
  const candidates: HeaderCandidate[] = []

  headerCells.forEach((cell, column) => {
    const header = normalizeHeaderText(cellToText(cell))
    if (!header) return

    for (const field of FIELD_ORDER) {
      for (const rawSynonym of HEADER_SYNONYMS[field]) {
        const synonym = normalizeHeaderText(rawSynonym)
        const score = matchHeaderScore(header, synonym)
        if (score > 0) candidates.push({ field, column, score, synonymLength: synonym.length })
      }
    }
  })

  candidates.sort(
    (left, right) =>
      right.score - left.score ||
      right.synonymLength - left.synonymLength ||
      left.column - right.column ||
      FIELD_ORDER.indexOf(left.field) - FIELD_ORDER.indexOf(right.field),
  )

  const mapping = emptyColumnMapping(headerRow)
  const usedColumns = new Set<number>()
  for (const candidate of candidates) {
    if (mapping[candidate.field] !== null || usedColumns.has(candidate.column)) continue
    mapping[candidate.field] = candidate.column
    usedColumns.add(candidate.column)
  }
  return mapping
}

export function countMappedFields(mapping: ColumnMapping): number {
  return FIELD_ORDER.reduce((count, field) => count + (mapping[field] === null ? 0 : 1), 0)
}

function isNonEmptyRow(row: readonly unknown[]): boolean {
  return row.some((cell) => normalizeHeaderText(cellToText(cell)).length > 0)
}

/** Scans the first 30 non-empty rows and returns the strongest deterministic match. */
export function detectHeaderRow(
  rows: readonly (readonly unknown[])[],
  maxNonEmptyRows = 30,
): HeaderDetection | null {
  let inspectedNonEmptyRows = 0
  let best: HeaderDetection | null = null

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows.at(rowIndex)
    if (row === undefined || !isNonEmptyRow(row)) continue
    inspectedNonEmptyRows += 1
    if (inspectedNonEmptyRows > maxNonEmptyRows) break

    const headerRow = rowIndex + 1
    const mapping = detectColumnMapping(row, headerRow)
    const detectedFieldCount = countMappedFields(mapping)
    const identityBonus =
      (mapping.itemCodeColumn === null ? 0 : 1.5) +
      (mapping.itemNameColumn === null ? 0 : 1.5) +
      (mapping.itemDescriptionColumn === null ? 0 : 0.5)
    const score = detectedFieldCount + identityBonus
    const detection: HeaderDetection = {
      rowIndex,
      headerRow,
      mapping,
      detectedFieldCount,
      confidence: detectedFieldCount / FIELD_ORDER.length,
      score,
    }

    if (
      best === null ||
      detection.score > best.score ||
      (detection.score === best.score && detection.rowIndex < best.rowIndex)
    ) {
      best = detection
    }
  }

  return best !== null && best.detectedFieldCount >= 2 ? best : null
}

export const detectHeaderAndMapping = detectHeaderRow

export function hasIdentityMapping(mapping: ColumnMapping): boolean {
  return (
    mapping.itemNameColumn !== null ||
    mapping.itemDescriptionColumn !== null ||
    mapping.itemCodeColumn !== null
  )
}
